/**
 * PlayerSoul — "alma" que reemplaza el render del personaje cuando muere.
 *
 * El cliente oculta TODAS las capas físicas del personaje (ver
 * GameScene.updatePlayerSoulVisuals) y dibuja en su lugar este orbe etéreo:
 *
 *   - core   → núcleo brillante que pulsa.
 *   - emitter→ partículas chiquitas que, al moverse el alma, dejan una ESTELA
 *              direccional (reciben fuerza hacia ATRÁS del movimiento, como un
 *              aura con arrastre).
 *
 * El color principal depende de la FACCIÓN (Aresgard → rojo, Elvaria → azul,
 * newbie/rebelde → gris). El core usa una textura HORNEADA del atlas con el
 * fondo transparentado (sin recuadro negro); las wisps usan otra horneada.
 *
 * Es 100% client-side (ver docs/PARTICLES.md). Una instancia por player muerto.
 */

import Phaser from 'phaser';
import { PARTICLE_SHEET, getParticleFrameIndex } from './ParticleSystem';

// Frames del atlas particles1 (512x512 c/u → siempre escalar fuerte).
const FRAME_CORE = getParticleFrameIndex('flare1'); // 16 — brillo central
const FRAME_WISP = getParticleFrameIndex('light1'); // 17 — partícula de estela

// Keys de las texturas horneadas (fondo transparente) para core/wisp.
const CORE_TEX = 'soul_core_clean';
const WISP_TEX = 'soul_wisp_clean';

/** Paleta de color del alma según facción. */
export interface SoulPalette {
  core: number; // tint del núcleo
  tints: number[]; // tints de las partículas de estela (pick aleatorio)
}

const PALETTE_ARESGARD: SoulPalette = { core: 0xffe0e0, tints: [0xffd6d6, 0xff7a7a, 0xff3b3b] }; // rojo
const PALETTE_ELVARIA: SoulPalette = { core: 0xe0ecff, tints: [0xeaf3ff, 0x7aa8ff, 0x3b6bff] }; // azul
const PALETTE_NEUTRAL: SoulPalette = { core: 0xf2f2f2, tints: [0xffffff, 0xcccccc, 0x9a9a9a] }; // gris

/** Devuelve la paleta del alma según el id de facción del player. */
export function soulPaletteForFaction(faction: string | undefined | null): SoulPalette {
  if (faction === 'aresgard') return PALETTE_ARESGARD;
  if (faction === 'elvaria') return PALETTE_ELVARIA;
  return PALETTE_NEUTRAL; // rebelde / newbie / desconocida
}

// Ritmo de emisión de la estela (ms entre partículas): denso al moverse para
// que quede un rastro a lo largo del camino, esporádico (shimmer) al estar quieto.
const FREQ_IDLE = 90;
const FREQ_MOVING = 18;
const QTY_IDLE = 1;
const QTY_MOVING = 1;

// Las partículas salen casi quietas: se quedan donde se emiten y el alma las
// "deja atrás" al avanzar → rastro en la dirección del movimiento. Leve flote.
const RISE = 5;

// Estela: soltar una partícula cada X píxeles recorridos (espaciado parejo,
// independiente del framerate). Se emiten a lo largo del camino con emitParticleAt.
const TRAIL_SPACING = 6;

// Umbral: alpha por debajo de esto se descarta (mata el haze tenue del cuadro).
const BAKE_ALPHA_FLOOR = 14;
// Máscara radial: el alpha cae a 0 entre estos radios (fracción del medio-lado),
// garantizando esquinas/bordes 100% transparentes (sin recuadro ni bleed).
const MASK_INNER = 0.8;
const MASK_OUTER = 0.98;

/**
 * Hornea un frame del atlas a una textura propia LIMPIA (fondo transparente +
 * piso de alpha + máscara radial). Idempotente por `outKey`.
 */
function bakeCleanFrame(scene: Phaser.Scene, frameIndex: number, outKey: string): void {
  if (scene.textures.exists(outKey)) return;
  const src = scene.textures.get(PARTICLE_SHEET.key);
  const frame = src.get(frameIndex);
  const w = frame.cutWidth;
  const h = frame.cutHeight;
  if (!w || !h) return;
  const cv = scene.textures.createCanvas(outKey, w, h);
  if (!cv) return;
  const ctx = cv.context;
  const sourceImage = src.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  ctx.drawImage(sourceImage, frame.cutX, frame.cutY, w, h, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  const cx = w / 2;
  const cy = h / 2;
  const half = Math.min(w, h) / 2;
  for (let p = 0; p < px.length; p += 4) {
    const i = p / 4;
    const x = i % w;
    const y = (i / w) | 0;

    const lum = Math.max(px[p], px[p + 1], px[p + 2]); // brillo del píxel
    let a = Math.min(px[p + 3], lum); // fondo oscuro → transparente

    if (a < BAKE_ALPHA_FLOOR) a = 0; // descartar haze tenue

    if (a > 0) {
      const r = Math.hypot(x - cx, y - cy) / half;
      if (r >= MASK_OUTER) a = 0;
      else if (r > MASK_INNER) {
        const tNorm = (r - MASK_INNER) / (MASK_OUTER - MASK_INNER);
        const fall = 1 - tNorm * tNorm * (3 - 2 * tNorm); // smoothstep invertido
        a = Math.round(a * fall);
      }
    }

    px[p + 3] = a;
  }
  ctx.putImageData(data, 0, 0);
  cv.refresh();
}

export class PlayerSoul {
  private readonly core: Phaser.GameObjects.Sprite;
  private readonly emitter: Phaser.GameObjects.Particles.ParticleEmitter;
  // Fase aleatoria para que las almas no pulsen/floten todas en sincronía.
  private readonly phase: number = Math.random() * 6283;
  private lastMoving: boolean | null = null;
  // Tracking de posición para soltar la estela espaciada por distancia recorrida.
  private lastX: number | null = null;
  private lastY: number | null = null;
  private trailAccum = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, depth: number, palette: SoulPalette) {
    bakeCleanFrame(scene, FRAME_CORE, CORE_TEX);
    bakeCleanFrame(scene, FRAME_WISP, WISP_TEX);

    this.emitter = scene.add.particles(x, y, WISP_TEX, {
      lifespan: { min: 450, max: 900 },
      speed: { min: 0, max: 4 }, // casi quietas → quedan en el camino (estela)
      angle: { min: 0, max: 360 },
      scale: { start: 0.05, end: 0 }, // partículas chiquitas
      alpha: { start: 0.8, end: 0 },
      rotate: { min: 0, max: 360 },
      gravityY: -RISE, // leve flote hacia arriba (no las "vuela")
      frequency: FREQ_IDLE,
      quantity: QTY_IDLE,
      blendMode: Phaser.BlendModes.ADD,
      tint: palette.tints,
    });
    this.emitter.setScrollFactor(1);

    this.core = scene.add
      .sprite(x, y, CORE_TEX)
      .setOrigin(0.5)
      .setScrollFactor(1)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setTint(palette.core)
      .setScale(0.11)
      .setAlpha(0.95);

    this.setDepth(depth);
  }

  /** Acomoda las capas alrededor del depth base del personaje. */
  setDepth(depth: number): void {
    this.emitter.setDepth(depth - 0.01);
    this.core.setDepth(depth + 0.01);
  }

  setVisible(visible: boolean): void {
    this.core.setVisible(visible);
    this.emitter.setVisible(visible);
    this.emitter.emitting = visible;
  }

  /**
   * Alterna el blend del alma. ADD se ve perfecto normalmente, pero bajo el postFX
   * grayscale de "estar muerto" deja un recuadro negro; en ese caso usamos NORMAL
   * (compone bien dentro del FBO del postFX, sin cuadrado).
   */
  setAdditive(additive: boolean): void {
    const mode = additive ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL;
    this.core.setBlendMode(mode);
    this.emitter.setBlendMode(mode);
  }

  /**
   * Reposiciona y anima el alma. `x`/`y` es el punto donde flota (ya con el
   * offset de altura aplicado por el caller). `time` = scene.time.now.
   */
  update(x: number, y: number, depth: number, isMoving: boolean, time: number): void {
    const t = time + this.phase;
    const bob = Math.sin(t / 380) * 3; // flotación suave
    const yy = y + bob;

    this.core.setPosition(x, yy);
    this.emitter.setPosition(x, yy);

    const pulse = 0.5 + 0.5 * Math.sin(t / 260); // 0..1
    this.core.setScale(0.1 + pulse * 0.025);
    this.core.setAlpha(0.85 + pulse * 0.15);

    this.setDepth(depth);

    // ESTELA: soltar partículas a lo largo del recorrido (no del bob vertical),
    // espaciadas por distancia. Como se emiten en el punto del camino y quedan
    // casi quietas, el alma deja un rastro detrás al avanzar — incluso aunque el
    // emisor la siga. Usamos `y` crudo (sin bob) para medir el desplazamiento real.
    if (this.lastX !== null && this.lastY !== null) {
      const dx = x - this.lastX;
      const dy = y - this.lastY;
      const dist = Math.hypot(dx, dy);
      if (dist > 0.001) {
        const ux = dx / dist;
        const uy = dy / dist;
        this.trailAccum += dist;
        while (this.trailAccum >= TRAIL_SPACING) {
          // Punto a `back` px DETRÁS de la posición actual (en el camino recorrido).
          const back = this.trailAccum;
          this.emitter.emitParticleAt(x - ux * back, y - uy * back);
          this.trailAccum -= TRAIL_SPACING;
        }
      }
    }
    this.lastX = x;
    this.lastY = y;

    // Emisión ambiente: densa al moverse (shimmer extra), esporádica al estar quieto.
    if (isMoving !== this.lastMoving) {
      this.lastMoving = isMoving;
      this.emitter.setFrequency(
        isMoving ? FREQ_MOVING : FREQ_IDLE,
        isMoving ? QTY_MOVING : QTY_IDLE
      );
    }
  }

  destroy(): void {
    this.core.destroy();
    this.emitter.destroy();
  }
}
